import { FC, useCallback, useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
// Hooks
import useToggle from 'hooks/useToggle';
// i18next
import { useTranslation } from 'react-i18next';
// Redux
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import { selectQuizEventsAsOptions } from 'store/quizEvent/quizEventSelectors';
import { getQuizEvents } from 'store/quizEvent/quizEventAsync';
import MediaFormats from 'types/MediaFormats';
// Components
import { Select, Textarea } from 'components/Controls';
import { Button } from 'components/Buttons';
import ImageUpload from 'components/ImageUpload';
import QuizEventDialog from 'dialogs/QuizEvent.dialog';
// Utilities
import { isRequired } from 'utilities/validation';

type Props = {
  waitingRef: string;
  quizId: string;
}

const WaitingForm: FC<Props> = ({ waitingRef, quizId }) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const quizEventsOptions = useAppSelector(selectQuizEventsAsOptions);

  const { control, watch, formState: { errors } } = useFormContext();

  const watchedWaitings = watch('waitings');
  const waitingIndex = watchedWaitings.findIndex((waiting: any) => waiting.ref === waitingRef);
  const name = `waitings.${waitingIndex}`;

  const getWaitingError = useCallback((field: string) => {
    if (!errors || !errors.waitings) return '';
    const waiting = (errors as any).waitings[waitingIndex];
    return waiting && waiting[field] ? waiting[field].message : '';
    // eslint-disable-next-line
  }, [errors]);

  const { open, toggle } = useToggle();

  useEffect(() => {
    if (quizId) dispatch(getQuizEvents(quizId));
    // eslint-disable-next-line
  }, [quizId]);

  return (
    <>
      <div className="w-full flex flex-wrap -mx-6 -y-4 px-6 pb-3">
        <div className="w-full landscape:w-1/2 tablet:w-full laptop:w-7/12 px-6">
          <div className="">
            <div className="mb-4 flex gap-4 items-center">
              <Controller
                control={control} name={`${name}.event`}
                rules={{ required: isRequired }}
                render={({ field }) => (
                  <Select
                    {...field}
                    label={t('labels.event')} id={`${name}.event`} name={`${name}.event`}
                    options={[
                      { value: '', label: t('labels.selectEvent'), disabled: true },
                      ...quizEventsOptions,
                    ]}
                    required
                    error={Boolean(getWaitingError('event'))}
                    errorText={getWaitingError('event')}
                    className="flex-grow"
                  />
                )}
              />
              <Button variant="contained" className="mt-5" onClick={toggle}>
                {t('buttons.createEvent')}
              </Button>
            </div>
            <Controller
              control={control}
              name={`${name}.text`}
              render={({ field }) => (
                <Textarea
                  {...field}
                  label={t('labels.text')}
                  id={`${name}.text`}
                  name={`${name}.text`}
                  rows={6}
                />
              )}
            />
          </div>
        </div>
        <div
          className="
            w-full landscape:w-1/2 tablet:w-full laptop:w-5/12
            landscape:border-l tablet:border-l-0 laptop:border-l
            px-6
          "
          style={{ borderColor: 'rgba(255,255,255,0.2)' }}
        >
          <Controller
            control={control}
            name={`${name}.image`}
            render={({ field }) => (
              <ImageUpload
                {...field}
                format={MediaFormats.CTA}
                label="labels.image"
              />
            )}
          />
        </div>
      </div>
      {open ? <QuizEventDialog open={open} onClose={toggle} quizId={quizId} /> : null}
    </>
  );
};

export default WaitingForm;
